import { Outlet, Link } from 'react-router';
import { Header } from '../components/Header';
import { GetStartedModal } from '../components/GetStartedModal';
import { useSiteConfig } from '../utils/siteConfig';
import { 
  Linkedin, 
  Youtube, 
  Facebook, 
  Twitter, 
  Instagram, 
  Github, 
  Globe 
} from 'lucide-react';

const socialIconMap: Record<string, any> = {
  LinkedIn: Linkedin,
  YouTube: Youtube,
  Facebook: Facebook,
  Twitter: Twitter,
  Instagram: Instagram,
  GitHub: Github,
  Meta: Facebook,
  Globe: Globe
};

const brandColors: Record<string, { text: string; bg: string; hoverBorder: string; hoverShadow: string }> = {
  LinkedIn: { text: 'text-[#0077b5]', bg: 'hover:bg-[#0077b5]', hoverBorder: 'hover:border-[#0077b5]', hoverShadow: 'hover:shadow-[#0077b5]/20' },
  YouTube: { text: 'text-[#ff0000]', bg: 'hover:bg-[#ff0000]', hoverBorder: 'hover:border-[#ff0000]', hoverShadow: 'hover:shadow-[#ff0000]/20' },
  Facebook: { text: 'text-[#1877f2]', bg: 'hover:bg-[#1877f2]', hoverBorder: 'hover:border-[#1877f2]', hoverShadow: 'hover:shadow-[#1877f2]/20' },
  Twitter: { text: 'text-[#1da1f2]', bg: 'hover:bg-[#1da1f2]', hoverBorder: 'hover:border-[#1da1f2]', hoverShadow: 'hover:shadow-[#1da1f2]/20' },
  Instagram: { text: 'text-[#e1306c]', bg: 'hover:bg-[#e1306c]', hoverBorder: 'hover:border-[#e1306c]', hoverShadow: 'hover:shadow-[#e1306c]/20' },
  GitHub: { text: 'text-[#181717]', bg: 'hover:bg-[#181717]', hoverBorder: 'hover:border-[#181717]', hoverShadow: 'hover:shadow-[#181717]/20' },
  Meta: { text: 'text-[#0668e1]', bg: 'hover:bg-[#0668e1]', hoverBorder: 'hover:border-[#0668e1]', hoverShadow: 'hover:shadow-[#0668e1]/20' }
};

export function Layout() {
  const { config } = useSiteConfig();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans selection:bg-emerald-500/30 overflow-x-hidden w-full">
      <Header />
      <GetStartedModal />
      <main className="flex-grow">
        <Outlet />
      </main>
      <footer className="relative overflow-hidden text-slate-700 pt-20 pb-10 px-4 md:px-8 mt-auto"
        style={{ background: `linear-gradient(160deg, color-mix(in srgb, var(--primary) 6%, #f8faff) 0%, color-mix(in srgb, var(--primary) 5%, #f0f4ff) 50%, color-mix(in srgb, var(--primary) 4%, #f8faff) 100%)` }}
      >
        {/* Decorative blobs */}
        <div className="absolute top-0 left-0 right-0 h-1 opacity-60"
          style={{ background: `linear-gradient(90deg, var(--primary), var(--primary), var(--primary))` }} />
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: `var(--primary)` }} />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full blur-3xl opacity-15 pointer-events-none"
          style={{ background: `var(--primary)` }} />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-12 gap-12 mb-12">

            {/* Brand Column */}
            <div className="col-span-12 md:col-span-3">
              <Link to="/" className="text-2xl mb-4 flex items-center gap-3">
                <img src={import.meta.env.BASE_URL + 'logo.png'} alt="BigGroww Logo" className="h-9 w-auto object-contain" />
              </Link>
              <p className="text-slate-500 text-sm leading-relaxed mb-6">{config.footer.tagline}</p>

              {/* Social Icons */}
              <div className="flex gap-3 flex-wrap">
                {config.contact.socials.map((social, index) => {
                  const Icon = socialIconMap[social.platform] || Globe;
                  const brand = brandColors[social.platform] || { text: 'text-slate-500', bg: 'hover:bg-slate-500', hoverBorder: 'hover:border-slate-500', hoverShadow: 'hover:shadow-slate-500/20' };
                  return (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-9 h-9 rounded-xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-sm ${brand.text} ${brand.bg} ${brand.hoverBorder} hover:text-white flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:scale-110 hover:shadow-md ${brand.hoverShadow}`}
                      title={social.platform}
                    >
                      <Icon size={16} />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Link Sections: 2 columns on mobile, 3 columns on desktop */}
            <div className="col-span-12 md:col-span-9 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-6 md:gap-12">
              {config.footer.sections.map((section, index) => (
                <div key={index}>
                  <h4
                    className="mb-4 text-xs md:text-sm font-bold uppercase tracking-wider md:tracking-widest animate-pulse-slow"
                    style={{ color: `var(--primary)` }}
                  >
                    {section.title}
                  </h4>
                  <div className="flex flex-col gap-2.5">
                    {section.links.map((link, lIndex) => {
                      const isExternal = link.href.startsWith('http') || link.href.startsWith('#');
                      const cls = "text-xs md:text-sm text-slate-500 hover:text-[var(--primary)] transition-colors hover:translate-x-1 block transition-transform duration-200";
                      return isExternal ? (
                        <a key={lIndex} href={link.href} className={cls}>{link.label}</a>
                      ) : (
                        <Link key={lIndex} to={link.href} className={cls}>{link.label}</Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-xs" dangerouslySetInnerHTML={{ __html: config.footer.copyrightText }} />
            <div className="flex items-center gap-1.5 text-xs font-semibold"
              style={{ color: `var(--primary)` }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ background: `var(--primary)` }} />
              Crafted with passion by BigGroww
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
